import { FC, useMemo, useState } from "react";

import { serverGetSectionScoreReport } from "api/api-server-assessment";
import { translateSectionScoreFullReport } from "components/materials/section/assessment/utils";
import { CourseMenu } from "components/menus/CourseMenu";
import { SectionMenu } from "components/menus/SectionMenu";
import { TermMenu } from "components/menus/TermMenu";
import { t } from "i18n/i18n";
import { IconText } from "mds/components/IconText";
import { SquareButton } from "mds/components/buttons/SquareButton";
import { ArrowDownToLineIcon } from "mds/icons";
import { useAppSelector } from "store/index";
import { courseSelectors, sectionSelectors, termSelectors } from "store/selectors";
import { toastErrorMessage } from "utils/alerts";
import { BUTTON_EVENT_CATEGORY, trackEvent } from "utils/amplitude";
import { downloadCSVFile } from "utils/file";
import { useCourseIdsInTerm } from "utils/hooks/use-course-ids-in-term";

export const StudentSectionScoresReport: FC = () => {
  const [selectedTermId, setSelectedTermId] = useState("");
  const [selectedCourseId, setSelectedCourseId] = useState("");
  const [selectedSectionId, setSelectedSectionId] = useState("");
  const terms = useAppSelector(termSelectors.selectAll);
  const courses = useAppSelector(courseSelectors.selectAll);
  const sections = useAppSelector(sectionSelectors.selectAll);
  const getCourseIdsInTerm = useCourseIdsInTerm();
  const courseIdsInTerm = useMemo(
    () => getCourseIdsInTerm(selectedTermId),
    [getCourseIdsInTerm, selectedTermId],
  );

  const handleTermChange = (termId: string) => {
    setSelectedTermId(termId);
    setSelectedSectionId("");
    if (!getCourseIdsInTerm(termId).has(selectedCourseId)) setSelectedCourseId("");
  };

  const handleCourseChange = (courseId: string) => {
    setSelectedCourseId(courseId);
    setSelectedSectionId("");
  };

  const handleDownloadCSV = async () => {
    if (!selectedTermId) {
      toastErrorMessage(t("assessment_summary_report.select_term_to_view_data"));
      return;
    }

    try {
      // When a specific section is selected, pass section_id alone (omitting term/course
      //  to avoid a UniverseError) and use report_type=admin for the full admin column format.
      const params = selectedSectionId
        ? { section_id: selectedSectionId, report_type: "admin" as const }
        : {
            term_id: selectedTermId,
            ...(selectedCourseId ? { course_id: selectedCourseId } : {}),
            report_type: "admin" as const,
          };

      const reportData = await serverGetSectionScoreReport(params);
      const csvData = translateSectionScoreFullReport(reportData);
      if (csvData.rows.length === 0) {
        toastErrorMessage(t("assessment_summary_report.no_data"));
        return;
      }

      const sanitize = (s: string) => s.replace(/[^a-zA-Z0-9]/g, "");
      const selectedTerm = terms.find((term) => term.id === selectedTermId);
      const selectedCourse = courses.find((c) => c.id === selectedCourseId);
      const selectedSection = sections.find((s) => s.id === selectedSectionId);
      const parts = [
        "ForumStudentCourseGrades",
        sanitize(selectedTerm?.title || selectedTermId),
        selectedCourse && sanitize(selectedCourse.code),
        selectedSection && sanitize(selectedSection.title),
        new Date().toISOString().split("T")[0],
      ].filter(Boolean);
      const filename = `${parts.join("-")}.csv`;
      trackEvent("Home - Reports / Download section scores", {
        ...BUTTON_EVENT_CATEGORY,
        fileName: filename,
      });

      downloadCSVFile(csvData, filename);
    } catch {
      toastErrorMessage(t("assessment_summary_report.export_failed"));
    }
  };

  return (
    <div>
      <div className="mb-6 flex items-center justify-between">
        <h2 className="h3">{t("student_section_scores_report.title")}</h2>
      </div>

      {/* Filters */}
      <div className="mb-4 flex flex-wrap items-center gap-4">
        <div className="flex items-center gap-2">
          <TermMenu
            defaultToAll={false}
            eventPrefix="Home - Reports / "
            menuType="menu"
            selectedTermId={selectedTermId}
            size="s"
            onTermChange={handleTermChange}
          />
        </div>

        <div className="flex items-center gap-2">
          <CourseMenu
            disabled={!selectedTermId}
            filterBy={(c) => courseIdsInTerm.has(c.id)}
            menuType="menu"
            selectedCourseId={selectedCourseId}
            size="s"
            adminAccessibleOnly
            onCourseChange={handleCourseChange}
          />
        </div>

        <div className="flex items-center gap-2">
          <SectionMenu
            disabled={!selectedTermId}
            emptyOptionText={t("import_modal.all_sections")}
            filterBy={(s) =>
              s.term_id === selectedTermId &&
              (!selectedCourseId || s.course_id === selectedCourseId)
            }
            selectedSectionId={selectedSectionId}
            size="s"
            onSectionChange={setSelectedSectionId}
          />
        </div>

        <div className="flex items-center gap-2">
          <SquareButton
            disabled={!selectedTermId}
            eventName="Student Section Scores Report - Download CSV"
            kind="tertiary"
            size="s"
            disableWhilePending
            onClick={handleDownloadCSV}
          >
            <IconText
              iconEnd={<ArrowDownToLineIcon />}
              text={t("student_section_scores_report.download_csv")}
            />
          </SquareButton>
        </div>
      </div>
    </div>
  );
};
